import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { TranslationKeys } from '../../../i18n';

interface DeletedProjectWarningModalProps {
  open: boolean;
  projectName: string;
  deletedAt: string;
  deletedByName: string;
  currentProjectName?: string; // For update flow
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeletedProjectWarningModal: React.FC<DeletedProjectWarningModalProps> = ({
  open,
  projectName,
  deletedAt,
  deletedByName,
  currentProjectName,
  loading,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation<TranslationKeys>();
  const isUpdateFlow = Boolean(currentProjectName);

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color="warning" fontSize="large" />
            <Typography variant="h6" component="div">
              {t('modal.deleted_project_warning_title')}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onCancel}
            disabled={loading}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('modal.deleted_project_warning_message')}
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('modal.deleted_project_info')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
            {projectName}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            {t('modal.deleted_at')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {formatDate(deletedAt)}
          </Typography>

          <Typography variant="subtitle2" color="text.secondary">
            {t('modal.deleted_by')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {deletedByName}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.primary">
          {t('modal.deleted_project_action')}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={loading}
          color="primary"
        >
          {t('modal.cancel_operation')}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="warning"
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? t('processing') : t('modal.delete_and_continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeletedProjectWarningModal;
